import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE } from "../apiConfig";
import { useAuth } from "../auth/AuthContext";
import { linkRecoveryEmail } from "../auth/authApi";
import { dirForLang, normalizeLang } from "../i18n";
import { getSystemStrings } from "../strings/systemStrings";
import { getProblemByKey, getProblemTitleForLang } from "../journeyProblems";
import JourneyService from "../services/JourneyService";
import LegalFooterLinks from "../components/LegalFooterLinks";
import { SUPPORT_EMAIL } from "../legal/legalContent";
import "./Account.css";

const HE_ACCOUNT_FIX = {
  title: "חשבון",
  logout: "התנתקות",
  continueToNext: "המשך למסע",
  plansTitle: "מסלולים",
  plansTrialLabel: "התנסות",
  oneTimeUnlockCardTitle: "פתיחה חד-פעמית למסע אחד",
  entitlementsTitle: "פתיחות שכבר רכשת",
  openJourney: "פתיחת מסע",
  noEntitlements: "עדיין לא נרכשו פתיחות בתשלום.",
  loadingPlans: "טוען מסלולים...",
  noPlans: "אין עדיין מסלולים זמינים.",
  startMonthly: "חודשי",
  oneTime: "חד-פעמי",
  errorLoadPlans: "טעינת מסלולים נכשלה",
  errorCheckout: "הקופה נכשלה",
  identitySectionTitle: "החשבון שלך",
  identitySignedInAs: "נכנסת עם האימייל:",
  identityLoggedInEmailLabel: "אימייל",
  identityNoEmailPasswordHint:
    "כדי לאפס או להגדיר סיסמה צריך אימייל בחשבון — אפשר לקשר אימייל למטה.",
  accountSecurityTitle: "אבטחה וסיסמה",
  accountPasswordHint:
    "נשלח קוד לאימייל שלך, ואז תבחר סיסמה חדשה (מינימום 8 תווים).",
  resetPasswordCta: "איפוס סיסמה",
  displayNameLabel: "שם תצוגה:",
  identityDeviceOnly:
    "אין לחשבון הזה כרגע אימייל משויך. נכנסת אוטומטית לפי המכשיר — הנתונים והמסע שמורים כאן במכשיר הזה.",
  identityInternalId: "מזהה פנימי (לתמיכה)",
  linkEmailSectionTitle: "חיבור אימייל לחשבון",
  linkEmailHint:
    "שדה זה לא להתחברות עם סיסמה. הוא מאפשר לשמור כתובת אימייל על אותו חשבון: אפשר לשחזר גישה ממכשיר אחר עם קוד שנשלח לאימייל, ולקבל תזכורות למייל אם הפעלת אותן.",
  linkEmailFieldLabel: "כתובת האימייל לקישור",
  linkEmailLabel: "אימייל לשחזור",
  linkEmailPlaceholder: "הקלידו כתובת אימייל",
  linkEmailCta: "שמור אימייל",
  linkEmailLoading: "שומר...",
  remindersTitle: "תזכורות",
  remindersIntro: "אפשר לבחור איך ומתי לקבל תזכורות מהמסע שלך.",
  remindersEnabled: "קבל תזכורת יומית",
  remindersEmailEnabled: "שלח תזכורת למייל",
  remindersHour: "שעה",
  remindersMinute: "דקה",
  remindersTimezone: "אזור זמן",
  remindersLanguage: "שפת התזכורת",
  remindersSave: "שמור",
  remindersSendingTest: "שולח בדיקה...",
  remindersTest: "שלח תזכורת בדיקה",
  remindersSaved: "נשמר",
  remindersSaveFailed: "שמירה נכשלה",
  remindersTestSent: "תזכורת בדיקה נשלחה",
  remindersTestFailed: "שליחת בדיקה נכשלה",
  journeyHubTitle: "מה קורה עכשיו",
  activeJourneyTitle: "יש מסע שמחכה לך",
  activeJourneyBody:
    "אם תבחר להתחיל עכשיו, המערכת תחזיר אותך ישר ליום שמחכה לך.",
  newJourneyTitle: "עוד לא התחלת מסע פעיל",
  newJourneyBody:
    "אם זו הפעם הראשונה שלך, נתחיל במסך זיהוי קצר ומשם נוביל אותך למסע המתאים.",
  continueJourney: "להמשיך במסע",
  startJourney: "להתחיל מסע",
  chooseJourney: "לבחור מסע אחר",
  genderLabel: "מגדר",
  genderMale: "זכר",
  genderFemale: "נקבה",
  genderOther: "אחר",
  genderSave: "שמור מגדר",
  genderSaving: "שומר…",
  genderSaved: "המגדר נשמר.",
  genderSaveFailed: "שמירת המגדר נכשלה.",
  linkEmailSuccess: "האימייל קושר בהצלחה לחשבון.",
  subscriptionStatusTitle: "מנוי חודשי",
  subscriptionStatusActive: "פעיל",
  subscriptionStatusTrialing: "תקופת התנסות",
  subscriptionRenews: "חידוש/סיום תקופה",
  subscriptionCancelNotice: "המנוי יסתיים ללא חידוש בתום התקופה הנוכחית.",
  subscriptionPlanFallback: "מסלול",
  deviceAccountAuthHint:
    "רוצים חשבון עם אימייל וסיסמה כדי להיכנס מכל מכשיר? אפשר להירשם או להתחבר — והנתונים יישארו מקושרים אחרי שתקשרו את המכשיר לחשבון.",
  createFullAccount: "יצירת חשבון (אימייל)",
  alreadyHaveAccount: "יש לי כבר חשבון — התחברות",
  dataRightsHint:
    "מחיקת חשבון או קבלת עותק של הנתונים — פנו אלינו בדוא״ל:",
  footerTagline: "נשמח לשמוע ממך",
};

function planCardTitle(plan, lang, t) {
  if (lang !== "he") return plan.name;
  const hasMonthly = Boolean(plan.stripePriceIdMonthly);
  const hasOne = Boolean(plan.stripePriceIdOneTime);
  if (hasOne && !hasMonthly) return t.oneTimeUnlockCardTitle || plan.name;
  if (hasMonthly && !hasOne) return t.startMonthly || plan.name;
  if (hasMonthly && hasOne) return t.plansTitle || plan.name;
  return plan.name;
}

function formatSubscriptionDate(iso, lang) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const loc = lang === "he" ? "he-IL" : lang === "es" ? "es" : "en-US";
    return d.toLocaleDateString(loc, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

async function fetchUserSubscription() {
  const res = await fetch(`${API_BASE}/api/billing/user/subscription`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to load subscription");
  return res.json();
}

async function fetchPlans() {
  const res = await fetch(`${API_BASE}/api/billing/plans`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to load plans");
  return res.json();
}

async function createCheckout(payload) {
  const res = await fetch(`${API_BASE}/api/billing/checkout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Checkout failed");
  }
  return res.json();
}

async function fetchEntitlements() {
  const res = await fetch(`${API_BASE}/api/billing/entitlements`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to load entitlements");
  return res.json();
}

async function fetchReminderPreference() {
  const res = await fetch(`${API_BASE}/api/reminders/me`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to load reminders");
  return res.json();
}

async function saveReminderPreference(payload) {
  const res = await fetch(`${API_BASE}/api/reminders/me`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Failed to save reminders");
  return res.json();
}

async function sendReminderTest() {
  const res = await fetch(`${API_BASE}/api/reminders/test-send`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error("Failed to send reminder test");
  return res.json();
}

export default function Account() {
  const location = useLocation();
  const { user, logout, refresh, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [entitlements, setEntitlements] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [confirmingCheckout, setConfirmingCheckout] = useState(false);
  const [linkEmail, setLinkEmail] = useState("");
  const [linking, setLinking] = useState(false);
  const [reminders, setReminders] = useState({
    enabled: true,
    emailEnabled: true,
    language: "he",
    timezone: "UTC",
    reminderHour: 19,
    reminderMinute: 0,
  });
  const [savingReminders, setSavingReminders] = useState(false);
  const [testingReminder, setTestingReminder] = useState(false);
  
const [reminderMsg, setReminderMsg] = useState("");
const [genderMsg, setGenderMsg] = useState("");
const [savingGender, setSavingGender] = useState(false);
const [linkEmailMsg, setLinkEmailMsg] = useState("");

const [gender, setGender] = useState(user?.gender || "other");
useEffect(() => {
  setGender(user?.gender || "other");
}, [user?.gender]);

   const lang = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return normalizeLang(
      params.get("lang") ||
        (typeof window !== "undefined" ? window.localStorage.getItem("innervoice_lang") : "he")
    );
  }, [location.search]);
  const t = useMemo(() => {
    const base = getSystemStrings(lang).account || {};
    if (lang === "he") return { ...base, ...HE_ACCOUNT_FIX };
    return base;
  }, [lang]);

  const loginEmail = String(user?.email ?? "").trim();

  useEffect(() => {
    refresh?.({ silent: true }).catch(() => {});
  }, [refresh]);
  const resumeJourney = useMemo(
    () => JourneyService.getResumeJourney(lang),
    [lang, location.search]
  );
  const resumeProblemTitle = useMemo(
    () =>
      resumeJourney?.problemKey
        ? getProblemTitleForLang(resumeJourney.problemKey, lang, { includeDuration: false })
        : "",
    [lang, resumeJourney?.problemKey]
  );
  const journeyDayLabel = useMemo(() => {
    const copy = getSystemStrings(lang);
    return copy.journeyOverview?.dayLabel || copy.journeyDay?.dayLabel || "Day";
  }, [lang]);
  const nextPath = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get("next");
    if (!raw) return "";
    const decoded = decodeURIComponent(raw);
    if (!decoded.startsWith("/") || decoded.startsWith("/account")) return "";
    return decoded;
  }, [location.search]);
  const journeyHubTarget = useMemo(() => {
    if (resumeJourney?.problemKey) {
      return JourneyService.getReturnTarget(lang, resumeJourney.problemKey, {
        runId: resumeJourney.runId,
      });
    }
    return `/journey?lang=${encodeURIComponent(lang)}`;
  }, [lang, resumeJourney]);

  const reminderLangOptions = useMemo(
    () =>
      lang === "he"
        ? [
            { value: "he", label: "עברית" },
            { value: "en", label: "אנגלית" },
            { value: "es", label: "ספרדית" },
          ]
        : [
            { value: "he", label: "HE" },
            { value: "en", label: "EN" },
            { value: "es", label: "ES" },
          ],
    [lang]
  );

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchPlans(),
      fetchEntitlements(),
      fetchReminderPreference().catch(() => null),
      fetchUserSubscription().catch(() => ({ subscription: null })),
    ])
      .then(([plansData, entitlementData, remindersData, subData]) => {
        setPlans(plansData.plans || []);
        setEntitlements(entitlementData.entitlements || []);
        setSubscription(subData?.subscription ?? null);
        if (remindersData?.preference) {
          const p = remindersData.preference;
          setReminders({
            enabled: Boolean(p.enabled),
            emailEnabled: Boolean(p.emailEnabled),
            language: p.language || "he",
            timezone: p.timezone || "UTC",
            reminderHour: Number.isFinite(Number(p.reminderHour)) ? Number(p.reminderHour) : 19,
            reminderMinute: Number.isFinite(Number(p.reminderMinute))
              ? Number(p.reminderMinute)
              : 0,
          });
        }
      })
      .catch((err) => setError(err.message || t.errorLoadPlans))
      .finally(() => setLoading(false));
  }, [t.errorLoadPlans]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkout = params.get("checkout");
    const sessionId = params.get("session_id");
    const problemKey = params.get("problemKey");
    const day = Number(params.get("day") || 2);
    if (checkout !== "success") return;

    // Problem unlock success return path (legacy).
    if (problemKey) {
      const next = new URLSearchParams();
      next.set("lang", lang);
      next.set("problemKey", problemKey);
      next.set("day", String(day));
      navigate(`/journey/overview?${next.toString()}`, { replace: true });
      return;
    }

    // Plan/subscription success return path: confirm it (no webhook required).
    if (!sessionId) return;
    let active = true;
    setConfirmingCheckout(true);
    fetch(`${API_BASE}/api/billing/checkout/plan/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ sessionId }),
    })
      .then(async (res) => {
        const data = await res.json().catch(() => null);
        if (!active) return;
        if (!res.ok || !data?.ok) {
          setError(t.errorCheckout);
          return;
        }
        navigate(`/account?lang=${encodeURIComponent(lang)}`, { replace: true });
      })
      .catch(() => {
        if (!active) return;
        setError(t.errorCheckout);
      })
      .finally(() => {
        if (active) setConfirmingCheckout(false);
      });

    return () => {
      active = false;
    };
  }, [location.search, navigate, lang, t.errorCheckout]);

  const handleCheckout = async (planId, cadence) => {
    setError("");
    try {
      const frontendBase = typeof window !== "undefined" ? window.location.origin : "";
      const { url } = await createCheckout({ planId, cadence, lang, frontendBase });
      if (url) {
        window.location.href = url;
      }
    } catch (err) {
      setError(err.message || t.errorCheckout);
    }
  };

  const handleLogout = async () => {
    setError("");
    await logout();
    navigate("/auth/login", { replace: true });
  };

  const handleLinkEmail = async () => {
    setError("");
    setLinkEmailMsg("");
    if (!linkEmail.trim()) return;
    setLinking(true);
    try {
      await linkRecoveryEmail({ email: linkEmail.trim() });
      setLinkEmailMsg(t.linkEmailSuccess);
      setLinkEmail("");
      await refresh?.({ silent: true });
    } catch (err) {
      setError(err.message || t.errorCheckout);
    } finally {
      setLinking(false);
    }
  };

  const handleSaveGender = async () => {
    setGenderMsg("");
    setSavingGender(true);
    try {
      await updateProfile({ gender });
      setGenderMsg(t.genderSaved);
    } catch {
      setGenderMsg(t.genderSaveFailed);
    } finally {
      setSavingGender(false);
    }
  };

  const accountReturnPath = `/account?lang=${encodeURIComponent(lang)}`;
  const accountNextEncoded = encodeURIComponent(accountReturnPath);

  const handleSaveReminders = async () => {
    setReminderMsg("");
    setSavingReminders(true);
    try {
      await saveReminderPreference(reminders);
      setReminderMsg(t.remindersSaved);
    } catch {
      setReminderMsg(t.remindersSaveFailed);
    } finally {
      setSavingReminders(false);
    }
  };

  const handleTestReminder = async () => {
    setReminderMsg("");
    setTestingReminder(true);
    try {
      await sendReminderTest();
      setReminderMsg(t.remindersTestSent);
    } catch {
      setReminderMsg(t.remindersTestFailed);
    } finally {
      setTestingReminder(false);
    }
  };

  const subscriptionSummary = useMemo(() => {
    if (!subscription) return null;
    const planMeta = plans.find((p) => p.id === subscription.planId);
    const planTitle = planMeta ? planCardTitle(planMeta, lang, t) : t.subscriptionPlanFallback;
    const st = String(subscription.status || "").toLowerCase();
    const statusLabel =
      st === "trialing"
        ? t.subscriptionStatusTrialing
        : st === "active"
          ? t.subscriptionStatusActive
          : String(subscription.status || "").trim() || "—";
    const periodEnd = formatSubscriptionDate(subscription.currentPeriodEnd, lang);
    return {
      planTitle,
      statusLabel,
      periodEnd,
      cancelAtPeriodEnd: Boolean(subscription.cancelAtPeriodEnd),
    };
  }, [subscription, plans, lang, t]);

  return (
    <div className="account-page" dir={dirForLang(lang)}>
      <div className="account-card">
        <header className="account-card-header">
          <div className="account-card-header-main">
            <h2>{t.title}</h2>
          </div>
          <div className="account-card-header-actions">
            {nextPath && (
              <button
                className="account-continue"
                onClick={() => navigate(nextPath, { replace: true })}
              >
                {t.continueToNext}
              </button>
            )}
            <button type="button" className="account-logout" onClick={handleLogout}>
              {t.logout}
            </button>
          </div>
        </header>
        {user && (
          <section className="account-identity" aria-label={t.identitySectionTitle}>
            <h3 className="account-identity-title">{t.identitySectionTitle}</h3>
            {loginEmail ? (
              <div className="account-identity-email-block">
                <p className="account-identity-signed-line">{t.identitySignedInAs}</p>
                <p className="account-identity-email-primary">
                  <strong translate="no">{loginEmail}</strong>
                </p>
              </div>
            ) : (
              <>
                <p className="account-identity-body">{t.identityDeviceOnly}</p>
                <p className="account-identity-body account-muted">{t.identityNoEmailPasswordHint}</p>
                {user.id != null && (
                  <p className="account-identity-id account-muted">
                    {t.identityInternalId}: <code>{String(user.id)}</code>
                  </p>
                )}
              </>
            )}
            {user.displayName ? (
              <p className="account-identity-display account-muted">
                {t.displayNameLabel} <span translate="no">{user.displayName}</span>
              </p>
            ) : null}
            {loginEmail ? (
              <div className="account-security">
                <h4 className="account-security-title">{t.accountSecurityTitle}</h4>
                <p className="account-security-hint account-muted">{t.accountPasswordHint}</p>
                <div className="account-plan-actions account-security-actions">
                  <button
                    type="button"
                    className="account-logout"
                    onClick={() => {
                      const accountReturn = `/account?lang=${encodeURIComponent(lang)}`;
                      navigate(
                        `/auth/login?lang=${encodeURIComponent(lang)}&next=${encodeURIComponent(
                          accountReturn
                        )}&reset=1&email=${encodeURIComponent(loginEmail)}`
                      );
                    }}
                  >
                    {t.resetPasswordCta}
                  </button>
                </div>
              </div>
            ) : null}
          </section>
        )}
        {user && !loginEmail && (
          <div className="account-auth-upgrade account-plan">
            <p className="account-auth-upgrade-hint">{t.deviceAccountAuthHint}</p>
            <div className="account-plan-actions account-auth-upgrade-actions">
              <button
                type="button"
                className="account-logout"
                onClick={() =>
                  navigate(
                    `/auth/register?lang=${encodeURIComponent(lang)}&next=${accountNextEncoded}`
                  )
                }
              >
                {t.createFullAccount}
              </button>
              <button
                type="button"
                className="account-logout"
                onClick={() =>
                  navigate(`/auth/login?lang=${encodeURIComponent(lang)}&next=${accountNextEncoded}`)
                }
              >
                {t.alreadyHaveAccount}
              </button>
            </div>
          </div>
        )}
        <section className="account-journey-hub">
          <div className="account-journey-hub-title">
            {t.journeyHubTitle || "What happens next"}
          </div>
          <div className="account-journey-hub-card">
            <div className="account-journey-hub-copy">
              <div className="account-plan-title">
                {resumeJourney?.problemKey
                  ? t.activeJourneyTitle || "You have a journey waiting"
                  : t.newJourneyTitle || "You have not started an active journey yet"}
              </div>
              <div className="account-plan-desc">
                {resumeJourney?.problemKey
                  ? t.activeJourneyBody ||
                    "If you already started a journey, the system will take you back to the day waiting for you."
                  : t.newJourneyBody ||
                    "If this is your first time, we will start with a short recognition screen and guide you to the right journey."}
              </div>
              {resumeJourney?.problemKey && (
                <div className="account-journey-hub-problem">
                  {resumeProblemTitle}
                  {resumeJourney?.currentDay ? ` · ${journeyDayLabel} ${resumeJourney.currentDay}` : ""}
                </div>
              )}
            </div>
            <div className="account-plan-actions">
              <button onClick={() => navigate(journeyHubTarget)}>
                {resumeJourney?.problemKey
                  ? t.continueJourney || "Continue journey"
                  : t.startJourney || "Start journey"}
              </button>
              {resumeJourney?.problemKey && (
                <button onClick={() => navigate(`/journey?lang=${encodeURIComponent(lang)}&fresh=1`)}>
                  {t.chooseJourney || "Choose another journey"}
                </button>
              )}
            </div>
          </div>
        </section>
        {user && !loginEmail && (
          <div className="account-link-email">
            <h4 className="account-link-email-title">{t.linkEmailSectionTitle}</h4>
            <p className="account-link-email-hint account-muted">{t.linkEmailHint}</p>
            <div className="account-link-email-field">
              <label className="account-link-email-label" htmlFor="account-recovery-email">
                {t.linkEmailFieldLabel}
              </label>
              <input
                id="account-recovery-email"
                type="email"
                autoComplete="email"
                value={linkEmail}
                onChange={(e) => {
                  setLinkEmailMsg("");
                  setLinkEmail(e.target.value);
                }}
                placeholder={t.linkEmailPlaceholder}
              />
            </div>
            <div className="account-plan-actions account-link-email-actions">
              <button type="button" onClick={handleLinkEmail} disabled={linking || !linkEmail.trim()}>
                {linking ? t.linkEmailLoading : t.linkEmailCta}
              </button>
            </div>
            {linkEmailMsg ? <p className="account-flash-success">{linkEmailMsg}</p> : null}
          </div>
        )}
        {error && <div className="account-error">{error}</div>}
        {confirmingCheckout && <div className="account-muted">{t.loadingPlans}</div>}
        {lang === "he" && t.plansTrialLabel && (
          <p className="account-plans-section-kicker">{t.plansTrialLabel}</p>
        )}
        <h3>{t.plansTitle}</h3>
        {loading && <div className="account-muted">{t.loadingPlans}</div>}
        {!loading && subscription && subscriptionSummary && (
          <div className="account-subscription-status account-plan">
            <div className="account-plan-title">{t.subscriptionStatusTitle}</div>
            <div className="account-plan-desc">
              {subscriptionSummary.planTitle} · {subscriptionSummary.statusLabel}
              {subscriptionSummary.periodEnd
                ? ` · ${t.subscriptionRenews}: ${subscriptionSummary.periodEnd}`
                : ""}
            </div>
            {subscriptionSummary.cancelAtPeriodEnd ? (
              <div className="account-plan-desc account-subscription-cancel-notice">
                {t.subscriptionCancelNotice}
              </div>
            ) : null}
          </div>
        )}
        {!loading && plans.length === 0 && (
          <div className="account-muted">{t.noPlans}</div>
        )}
        <div className="account-plans">
          {plans.map((plan) => (
            <div key={plan.id} className="account-plan">
              <div className="account-plan-title">{planCardTitle(plan, lang, t)}</div>
              {plan.description && lang !== "he" && (
                <div className="account-plan-desc">{plan.description}</div>
              )}
              <div className="account-plan-actions">
                {plan.stripePriceIdMonthly && (
                  <button onClick={() => handleCheckout(plan.id, "monthly")}>
                    {t.startMonthly}
                  </button>
                )}
                {plan.stripePriceIdOneTime && (
                  <button onClick={() => handleCheckout(plan.id, "one_time")}>
                    {t.oneTime}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <h3>{t.entitlementsTitle}</h3>
        {!loading && entitlements.length === 0 && (
          <div className="account-muted">{t.noEntitlements}</div>
        )}
        <div className="account-plans">
          {entitlements.map((item) => {
            const problem = getProblemByKey(item.problemKey);
            const title = problem
              ? getProblemTitleForLang(problem, lang)
              : item.problemKey;
            const next = new URLSearchParams();
            next.set("lang", lang);
            next.set("problemKey", item.problemKey);
            return (
              <div key={`${item.problemKey}-${item.grantedAt}`} className="account-plan">
                <div className="account-plan-title">{title}</div>
                {lang !== "he" && (
                  <div className="account-plan-desc">{item.problemKey}</div>
                )}
                <div className="account-plan-actions">
                  <button onClick={() => navigate(`/journey/overview?${next.toString()}`)}>
                    {t.openJourney}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="account-field" style={{ marginTop: 12 }}>
          <label>
            {t.genderLabel || (lang === "es" ? "Genero" : lang === "he" ? "מגדר" : "Gender")}
          </label>
          <select
            value={gender}
            onChange={(e) => {
              setGenderMsg("");
              setGender(e.target.value);
            }}
          >
            <option value="male">
              {t.genderMale || (lang === "he" ? "זכר" : lang === "es" ? "Masculino" : "Male")}
            </option>
            <option value="female">
              {t.genderFemale || (lang === "he" ? "נקבה" : lang === "es" ? "Femenino" : "Female")}
            </option>
            <option value="other">
              {t.genderOther || (lang === "he" ? "אחר" : lang === "es" ? "Otro" : "Other")}
            </option>
          </select>
          <div className="account-plan-actions account-gender-actions">
            <button type="button" onClick={handleSaveGender} disabled={savingGender}>
              {savingGender
                ? t.genderSaving
                : t.genderSave || t.saveGender || (lang === "es" ? "Guardar genero" : "Save gender")}
            </button>
          </div>
          {genderMsg ? (
            <p
              className={
                genderMsg === t.genderSaveFailed ? "account-flash-error" : "account-flash-success"
              }
            >
              {genderMsg}
            </p>
          ) : null}
        </div>

        <h3>{t.remindersTitle}</h3>
        {t.remindersIntro ? (
          <p className="account-reminders-intro account-muted">{t.remindersIntro}</p>
        ) : null}
        <div className="account-plan account-reminders">
          <label className="account-reminders-check">
            <input
              type="checkbox"
              checked={Boolean(reminders.enabled)}
              onChange={(e) => setReminders((prev) => ({ ...prev, enabled: e.target.checked }))}
            />
            <span>{t.remindersEnabled}</span>
          </label>
          <label className="account-reminders-check">
            <input
              type="checkbox"
              checked={Boolean(reminders.emailEnabled)}
              onChange={(e) =>
                setReminders((prev) => ({ ...prev, emailEnabled: e.target.checked }))
              }
            />
            <span>{t.remindersEmailEnabled}</span>
          </label>

          <div className="account-reminders-grid">
            <label className="account-reminder-field">
              <span>{t.remindersLanguage}</span>
              <select
                value={reminders.language}
                onChange={(e) =>
                  setReminders((prev) => ({ ...prev, language: e.target.value }))
                }
              >
                {reminderLangOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="account-reminder-field account-reminder-field-wide">
              <span>{t.remindersTimezone}</span>
              <input
                type="text"
                value={reminders.timezone}
                onChange={(e) =>
                  setReminders((prev) => ({ ...prev, timezone: e.target.value }))
                }
                placeholder="UTC"
              />
            </label>

            <label className="account-reminder-field">
              <span>{t.remindersHour}</span>
              <input
                type="number"
                min={0}
                max={23}
                value={reminders.reminderHour}
                onChange={(e) =>
                  setReminders((prev) => ({ ...prev, reminderHour: Number(e.target.value || 0) }))
                }
              />
            </label>

            <label className="account-reminder-field">
              <span>{t.remindersMinute}</span>
              <input
                type="number"
                min={0}
                max={59}
                value={reminders.reminderMinute}
                onChange={(e) =>
                  setReminders((prev) => ({
                    ...prev,
                    reminderMinute: Number(e.target.value || 0),
                  }))
                }
              />
            </label>
          </div>

          <div className="account-plan-actions">
            <button onClick={handleSaveReminders} disabled={savingReminders}>
              {t.remindersSave}
            </button>
            <button onClick={handleTestReminder} disabled={testingReminder}>
              {testingReminder ? t.remindersSendingTest : t.remindersTest}
            </button>
          </div>
          {reminderMsg && <div className="account-muted">{reminderMsg}</div>}

        <div className="account-legal-block">
          {t.footerTagline && <p className="account-footer-tagline">{t.footerTagline}</p>}
          <p className="account-data-rights account-muted">
            {t.dataRightsHint}{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </p>
          <p className="account-legal-support">
            <a href={`mailto:${SUPPORT_EMAIL}`}>
              {lang === "he" ? "תמיכה:" : lang === "es" ? "Soporte:" : "Support:"}{" "}
              {SUPPORT_EMAIL}
            </a>
          </p>
          <LegalFooterLinks />
        </div>

        </div>
      </div>
    </div>
  );
}
